<script setup>
import {useRoute} from "vue2-helpers/vue-router";
import trackersService from "@/services/trackers.service";
import {computed, onMounted, provide, reactive, ref, watch} from "vue";
import StartupsGrid from "@/views/trackers/tracker/StartupsGrid.vue";
import StartupsBoard from "@/views/trackers/tracker/StartupsBoard.vue";
import useFilters from "@/views/trackers/tracker/useFilters";
import TopBar from "@/views/trackers/tracker/TopBar.vue";
import StartupsTimeline from "@/views/trackers/tracker/StartupsTimeline.vue";
import trackersCompaniesService from "@/services/trackers-companies.service";
import useTrigger from "@/composables/useTrigger";
import AddStartups from "@/components/trackers/AddStartups.vue";
import AddEcosystems from "@/components/trackers/AddEcosystems.vue";
import {useStore} from "vue2-helpers/vuex";
import {trackerTypes} from "@/constants/trackers";
import SideBar from "@/views/trackers/tracker/SideBar.vue";
import TopBarActions from "@/views/trackers/tracker/TopBarActions.vue";
import EditTracker from "@/components/trackers/trackerEdit/TrackerEdit.vue";
import Approval from "@/views/trackers/tracker/Approval.vue";
import useStoreTrigger from "@/composables/useStoreTrigger";
import StartChatPopup from "@/views/messages/StartChatPopup";

const route = useRoute();
const store = useStore();
const storeTrigger = useStoreTrigger();
const [companiesListUpdated, setCompaniesListUpdated] = useTrigger();
const [trackerUpdated, setTrackerUpdated] = useTrigger();

const displayType = ref(null);
const addStartupsModal = ref(null);
const addEcosystemsModal = ref(null);
const tracker = ref(null);
const updatingCompanies = ref(false);
const {filters, serverFilters, resetForm: resetFilters, formFilled: filtersApplied} = useFilters();
const selectedRows = ref([]);
const selectedAllResults = ref(false);
const hasNotEmbargoed = ref(false);
const selectedStartupName = ref(null);
const columns = ref([]);
const sharedEcosystems = ref([]);
const companiesIds = ref(null);
const chat = ref(null);
const filteredCompaniesCount = ref(null);
const showSideBar = ref(true);

const addCellData = reactive({
    trackerId: 0,
    trackerType: "",
    userPermissions: "",
    companyType: "startups",
    trackersList: [],
});

const companiesCount = computed(() => companiesIds.value === null ? null : companiesIds.value.length);
const trackerId = computed(() => tracker.value?.id);

watch(displayType, () => {
    selectedRows.value = [];
    selectedAllResults.value = false;
});

watch(companiesListUpdated, (value) => {
    if (value) {
        getCompaniesIds();
        updateLogs();
    }
});

onMounted(() => {
    getInfo();
});

async function getInfo() {
    const {data} = await trackersService.getInfo(route.params.id);
    tracker.value = data;

    if (tracker.value.needs_approval) {
        return;
    }

    addCellData.trackerId = tracker.value.id;
    addCellData.trackerType = tracker.value.type;
    addCellData.userPermissions = tracker.value.user_permissions;

    if (tracker.value.type === trackerTypes.ECOSYSTEMS) {
        addCellData.companyType = 'ecosystems';
    }

    displayType.value = data.default_view;
    document.title = tracker.value.name + ' | SwitchPitch';

    getCompaniesIds();

    if (store.getters.isEnterprise) {
        getSharedEcosystems();
        store.dispatch('fetchTags');
    }
}

async function getCompaniesIds() {
    const {data} = await trackersCompaniesService.getCompaniesIds(tracker.value.id);
    companiesIds.value = data;
}

function addStartups() {
    addStartupsModal.value.open();
}

function addStartupsToStage(stage = null) {
    addStartupsModal.value.open(stage);
}

function addEcosystems() {
    addEcosystemsModal.value.open();
}

async function addCompany(companyId) {
    updatingCompanies.value = true;
    await trackersCompaniesService.addCompanies([tracker.value.id], [companyId]);

    setCompaniesListUpdated();
    updatingCompanies.value = false;
}

async function removeCompany(companyId) {
    updatingCompanies.value = true;
    await trackersCompaniesService.removeCompanies(tracker.value.id, [companyId])

    setCompaniesListUpdated();
    updatingCompanies.value = false;
    storeTrigger('setNeedUpdateTrackerLogs');
}

async function getSharedEcosystems() {
    const {data} = await trackersService.getSharedEcosystems(tracker.value.id);
    sharedEcosystems.value = data;

    if (sharedEcosystems.value.length && tracker.value.access_type === 'Private') {
        tracker.value.access_type = 'Custom';
    }
}

function trackerInfoChanged() {
    setTrackerUpdated();
    getInfo();
}

function startupsAdded() {
    setCompaniesListUpdated();
    storeTrigger('setNeedUpdateTrackerLogs');
}

function openChat(startupId) {
    chat.value.open(startupId);
}

function updateLogs() {
    storeTrigger('setNeedUpdateTrackerLogs');
}

provide('tracker', tracker);
provide('currentTrackerId', trackerId);
provide('displayType', displayType);
provide('companiesIds', companiesIds);
provide('updatingCompanies', updatingCompanies);
provide('addCellData', addCellData);
provide('filters', filters);
provide('serverFilters', serverFilters);
provide('companiesCount', companiesCount);
provide('filteredCompaniesCount', filteredCompaniesCount);
provide('filtersApplied', filtersApplied);
provide('companiesListUpdated', companiesListUpdated);
provide('selectedRows', selectedRows);
provide('selectedAllResults', selectedAllResults);
provide('columns', columns);
provide('sharedEcosystems', sharedEcosystems);
provide('hasNotEmbargoed', hasNotEmbargoed);
provide('selectedStartupName', selectedStartupName);
provide('trackerUpdated', trackerUpdated);
provide('showSideBar', showSideBar);

provide('getSharedEcosystems', getSharedEcosystems);
provide('addCompany', addCompany);
provide('addStartups', addStartups);
provide('addStartupsToStage', addStartupsToStage);
provide('addEcosystems', addEcosystems);
provide('removeCompany', removeCompany);
provide('setCompaniesListUpdated', setCompaniesListUpdated);
provide('trackerInfoChanged', trackerInfoChanged);
provide('resetFilters', resetFilters);
provide('openChat', openChat);
</script>

<template>
    <div v-if="tracker" class="">
        <div :id="displayType" class="tracker-type">
            <TopBar/>

            <TopBarActions/>
            <div class="tracker-type__scroll-section" :class="{'tracker-type__scroll-section--poc': tracker.type === trackerTypes.POC}">
                <SideBar/>

                <div class="tracker-table-slide">
                    

                    <div class="scroll-anchor"></div>

                    <StartupsTimeline
                        v-if="displayType === 'timeline'"
                        @timelineChanged="updateLogs"
                    />

                    <main class="main main--dark main--dark--tracker">
                        <Approval
                            v-if="tracker.needs_approval"
                            :id="tracker.id"
                            @approved="getInfo"
                        />

                        <StartupsGrid
                            v-if="displayType === 'list' && companiesCount !== null"
                            @startupStageChanged="updateLogs"
                            @startupPocStatusChanged="updateLogs"
                        />

                        <StartupsBoard
                            v-if="displayType === 'pipeline'"
                            @reviewUpdated="updateLogs"
                        />
                    </main>
                </div>
            </div>
        </div>

        <EditTracker
            ref="editTracker"
            @trackerSaved="trackerInfoChanged"
        />

        <AddStartups
            ref="addStartupsModal"
            :trackerId="tracker.id"
            @startupsAdded="startupsAdded"
        />

        <AddEcosystems
            v-if="store.getters.isEnterprise"
            ref="addEcosystemsModal"
            :trackerId="tracker.id"
            @ecosystemsAdded="setCompaniesListUpdated"
        />

        <StartChatPopup
            ref="chat"
            contactsType="startup"
        />
    </div>
</template>

<style scoped>

</style>
